import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams } from "ag-grid-enterprise";
import { useRecoilState, useRecoilValue } from "recoil";
import { FilterDef } from "../../../constants/FinanceModels";
import * as Mui from "@mui/material";
import { useTheme } from "@mui/material";
import { formatCurrency } from "../../../utils/FormatUtils";
import { getLocalGridState, setLocalGridState } from "../../../utils/GridUtils";
import { STATE_LIVEMONITORING_UPDATING } from "../constants/CashbackRecoil";
import { STATE_ACTIVE_FILTERS } from "../constants/LiveMonitoringConfigurationRecoil";
import { useCashBackRepository } from "../../../api/repositories/CashbackRepository";
import EmptyState from "../../../../../util/component/EmptyState";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import { useLocation } from "react-router-dom";
import * as MuiIcons from "@mui/icons-material";
import useToggle from "../../../../operation/hooks/useToggle";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import EditNote from "../sections/EditNote";
import { convertTypology } from "../util/CashbackUtil";
import { useAgGridLocale } from "../../../../../config/globalConfig";


const LiveMonitoringTable = () => {

    const UNIQUE_ID = "fnn-cashback-livemonitoring";

    const { dispatchGlobalData } = useContext(GlobalContext);

    const { t } = useTranslation('finance', { keyPrefix: 'cashback' });
    const { getLiveMonitoring } = useCashBackRepository();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const dateFrom = queryParams.get('dateFrom');
    const dateTo = queryParams.get('dateTo');
    const [currentRow, setCurrentRow] = useState<any | undefined>(undefined);
    const [updating, setUpdating] = useRecoilState(STATE_LIVEMONITORING_UPDATING);

    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);

    const {  globalData: { user } } = useContext(GlobalContext);
    const legalEntities: any[] = user.legalEntities?.filter((x: any) => x.name.toLowerCase() !== "all");
    const dealers: any[] = user.dealers?.filter((x: any) => x.name.toLowerCase() !== "all");

    const [showEditNotes, toggleEdit] = useToggle();
    const gridRefMonitoring = useRef<AgGridReact>(null);
    let lastCustomerName = "";
    let lastSapId = "";
    let currentPageNumberCn = 0;
    let currentPageNumberId = 0;
    const agGridLocale = useAgGridLocale();

    const columnDefsMonitoring: ColDef[] = [
        {
            field: 'customer-name', headerName: t('tables.livemonitoring.customer-name'), headerTooltip: t('tables.livemonitoring.customer-name'),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellRenderer: (params: any) => {
                const customerName = params.value;
                const pageNumber = params.api.paginationGetCurrentPage();

                if (customerName === lastCustomerName && pageNumber === currentPageNumberCn && params.rowIndex !== 0) {
                  return '';
                }

                lastCustomerName = customerName;
                currentPageNumberCn = pageNumber;
                return customerName;
              }
        },
        {
            field: 'sap-id', headerName: t('tables.livemonitoring.sap-id'), headerTooltip: t('tables.livemonitoring.sap-id'),
            minWidth: 200,
            unSortIcon: true,
            sortable: false,
            cellRenderer: (params: any) => {
                const sapId = params.value;
                const pageNumber = params.api.paginationGetCurrentPage();

                if (sapId === lastSapId && pageNumber === currentPageNumberId && params.rowIndex !== 0) {

                  return '';
                }
                lastSapId = sapId;
                currentPageNumberId = pageNumber;
                return sapId;
              }
        },

        {
            field: 'typology', headerName: t('tables.livemonitoring.typology'), headerTooltip: t('tables.livemonitoring.typology'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => t(convertTypology(params.value))
        },
        {
            field: 'taxable', headerName: t('tables.livemonitoring.taxable'), headerTooltip: t('tables.livemonitoring.taxable'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatCurrency(params.value, 'EUR', 0.01),
        },
        {
            field: 'threshold', headerName: t('tables.livemonitoring.threshold-value'), headerTooltip: t('tables.livemonitoring.threshold-value'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatCurrency(params.value, 'EUR', 0.01)
        },
        {
            field: 'percentage', headerName: t('tables.livemonitoring.percentage'), headerTooltip: t('tables.livemonitoring.percentage'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => `${params.value}%`
        },
        {
            field: 'accrued-amount', headerName: t('tables.livemonitoring.accrued-amount'), headerTooltip: t('tables.livemonitoring.accrued-amount'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatCurrency(params.value, 'EUR', 0.01)
        },
        {
            field: "legalEntity", headerName: t("tables.livemonitoring.legal-entity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: legalEntities?.length <= 1
        },
        {
            field: "dealer", headerName: t("tables.livemonitoring.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: dealers?.length <= 1
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                function openEditModalNotes(params: any): void {
                    setCurrentRow(params.data);
                    toggleEdit();
                }
                const sapId = params.data['sap-id'];
                const pageNumber = params.api.paginationGetCurrentPage();
                if (sapId === lastSapId && pageNumber === currentPageNumberId && params.rowIndex !== 0) {
                    return {
                        component: () =>
                            <FinanceDynamicActions>
                            </FinanceDynamicActions>
                    };
                  } 
                  lastSapId = sapId;
                  currentPageNumberId = pageNumber;
                    return {
                        component: () =>
                            <FinanceDynamicActions>
                                <Mui.Tooltip title={t("tables.livemonitoring.notes")}>
                                    <Mui.IconButton color="accent" onClick={() => openEditModalNotes(params)}>
                                        <MuiIcons.DescriptionRounded />
                                    </Mui.IconButton>
                                </Mui.Tooltip>
                            </FinanceDynamicActions>
                    };
                


            },
            suppressFiltersToolPanel: true
        }
    ];

    const gridLMOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => {
            return `${params.data['id']}+${Math.random()}`

        },
        defaultColDef: {
            sortable: false,
            resizable: true,
            wrapHeaderText: false,
        },
        localeText: agGridLocale,
        noRowsOverlayComponentParams: { title: t('tables.livemonitoring.no-rows.title'), content: t('tables.livemonitoring.no-rows.content') }
    };




    useEffect(() => {
        gridRefMonitoring.current?.api?.setServerSideDatasource(createLiveMonitoringDatasource(filterDefs, gridRefMonitoring.current.api));

    }, [filterDefs])

    useEffect(() => {
        if (updating) {
            gridRefMonitoring.current?.api?.setServerSideDatasource(createLiveMonitoringDatasource(filterDefs, gridRefMonitoring.current.api));
            setUpdating(false);
        }
    }, [updating])




    const createLiveMonitoringDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {

                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort.toUpperCase();

                getLiveMonitoring(startRow, endRow - startRow, sort, order, filterDefs, dateFrom ? dateFrom : "", dateTo ? dateTo : "")
                    .then(response => {
                        let result = response;
                  
                        if (result['total-amount'] === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result['total-amount']
                        })
                    })
                    .catch(() => params.fail());
            }
        };
    }

    function saveMonitoringState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRefMonitoring.current!.api.paginationGetPageSize(),
            cols: gridRefMonitoring.current!.columnApi.getColumnState()
        });
    }

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);


    return (
        <Mui.Box>
            <AgGridCustomTable
                rowModelType='serverSide'
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                gridRef={gridRefMonitoring} columnDefs={columnDefsMonitoring} gridOptions={gridLMOptions}
                rowSelection={'multiple'}
                noRowsOverlayComponent={noRowsOverlayComponent}
                suppressRowClickSelection

                onGridReady={() => {
                    gridRefMonitoring.current!.api.setServerSideDatasource(createLiveMonitoringDatasource(filterDefs, gridRefMonitoring.current!.api));

                    let gridState = getLocalGridState(UNIQUE_ID).cols;
                    if (gridState !== undefined) {
                        gridRefMonitoring.current!.columnApi.applyColumnState({
                            state: gridState,
                            applyOrder: true
                        })
                    }
                }}
                onColumnMoved={e => {
                    if (e.source === "api") return;
                    saveMonitoringState();
                }}
                onColumnPinned={e => {
                    if (e.source === "api") return;
                    saveMonitoringState();
                }}
                onColumnResized={e => {
                    if (e.source === "api") return;
                    saveMonitoringState();
                }}
                onSortChanged={e => {
                    if (e.source === "api") return;
                    saveMonitoringState();
                }}
                onPaginationChanged={() => {
                
                    if (gridRefMonitoring.current?.api) {
                        if (gridRefMonitoring.current!.api.paginationGetPageSize() < 10) {
                            gridRefMonitoring.current!.api.paginationSetPageSize(10);
                        }
                        setLocalGridState(UNIQUE_ID, {
                            rows: gridRefMonitoring.current!.api.paginationGetPageSize(),
                            cols: getLocalGridState(UNIQUE_ID).cols
                        });
                    }
                }}
            />
            {showEditNotes &&
                <EditNote
                    open={showEditNotes}
                    onClose={toggleEdit}
                    currentRow={currentRow}
                />
            }

        </Mui.Box>
    );
}


export default LiveMonitoringTable;